// // LICENSE_CODE ZON
'use strict'; /*jslint ts:true, react:true*/
import React, {useEffect, useMemo, useRef, useState, Fragment} from 'react';
import cn from 'classnames';
import styles from './compare_table.module.less';
import {Editor} from '../../../shared/editor/editor';

const columns = [
    {key: 'system', label: 'System-wide'},
    {key: 'setup', label: 'Setup complexity'},
    {key: 'protection', label: 'Protection level'},
    {key: 'best_use', label: 'Best use case'},
    {key: 'platform', label: 'Platform Support'},
];

const rows = [
    {
        name: 'Browser Extension',
        size: 'compact',
        system: false,
        setup: 'Lowest',
        protection: 'Proxy Split Tunnelling (HTTPS only)',
        best_use: 'Unblock specific sites fast within your browser',
        platform: 'Chrome, Edge, Opera (latest 1-2 versions)',
    },
    {
        name: 'Hola Browser',
        size: 'compact',
        system: false,
        setup: 'Low',
        protection: 'SSL (HTTPS Proxy) traffic secured by HTTPS',
        best_use: 'Browser for secure access to any site',
        platform: 'Windows 10+, macOS 11+',
    },
    {
        name: 'Desktop App',
        size: 'regular',
        system: true,
        setup: 'Low',
        protection: 'Full VPN Tunnel (IKEv2/IPSec, default AES-256 '
            +'encryption)',
        best_use: 'Full PC/Mac coverage for all apps and programs',
        platform: 'Windows 10+, macOS 11+',
    },
    {
        name: 'iOS App',
        size: 'regular',
        system: true,
        setup: 'Low',
        protection: 'Full VPN Tunnel (IKEv2/IPSec, default AES-256 '
            +'encryption)',
        best_use: 'System-wide access and global content on-the-go',
        platform: 'iOS 12.0+',
    },
    {
        name: 'Android App',
        size: 'regular',
        system: false,
        setup: 'Low',
        protection: 'Proxy Split Tunnelling (Select traffic routed, not '
            +'all traffic encrypted)',
        best_use: 'On-the-go access to mobile-specific global content',
        platform: 'Android 5.0+',
    },
    {
        name: 'Apple TV',
        size: 'regular',
        system: true,
        setup: 'Low',
        protection: 'Full VPN Tunnel (IKEv2/IPSec, default AES-256 '
            +'encryption)',
        best_use: 'System-wide streaming on Apple TV for all apps',
        platform: 'tvOS [min]+',
    },
    {
        name: 'Android TV/Fire TV',
        size: 'regular',
        system: false,
        setup: 'Low',
        protection: 'Proxy Split Tunnelling (Select traffic routed, not'
            +' all traffic encrypted)',
        best_use: 'Streaming access for specific Android/Fire TV apps',
        platform: 'Android TV [min]+, Fire OS [min]+',
    },
    {
        name: 'Other Devices',
        name_note: '(Smart TVs, Consoles, Routers)',
        size: 'regular',
        system: false,
        setup: 'Medium',
        protection: 'Smart DNS (No encryption)',
        best_use: 'Whole-home coverage for consoles and all devices',
        platform: 'Other Smart TVs, Xbox, PlayStation, Routers (see setup '
            +'guides)',
    },
];

const Icon = ({type}: {type: 'yes' | 'no'})=>{
    if (type=='yes')
    {
        return <svg className={cn(styles.icon, styles.icon_yes)}
          xmlns="http://www.w3.org/2000/svg" width="16" height="16"
          viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path fillRule="evenodd" clipRule="evenodd"
            d="M14.4592 3.53999C14.7131 3.79383 14.7131 4.20539 14.4592
            4.45923L6.45923 12.4592C6.20539 12.7131 5.79383 12.7131 5.53999
            12.4592L1.53999 8.45923C1.28615 8.20539 1.28615 7.79383 1.53999
            7.53999C1.79383 7.28615 2.20539 7.28615 2.45923 7.53999L5.99961
            11.0804L13.54 3.53999C13.7938 3.28615 14.2054 3.28615 14.4592
            3.53999Z" fill="currentColor" />
        </svg>;
    }
    return <svg className={cn(styles.icon, styles.icon_no)}
      xmlns="http://www.w3.org/2000/svg" width="16" height="16"
      viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path fillRule="evenodd" clipRule="evenodd"
        d="M3.54001 3.53999C3.79385 3.28615 4.20541 3.28615 4.45925
        3.53999L7.99963 7.08037L11.54 3.53999C11.7939 3.28615 12.2054
        3.28615 12.4593 3.53999C12.7131 3.79383 12.7131 4.20539 12.4593
        4.45923L8.91887 7.99961L12.4592 11.54C12.7131 11.7938 12.7131
        12.2054 12.4592 12.4592C12.2054 12.713 11.7938 12.713 11.54
        12.4592L7.99963 8.91885L4.45923 12.4593C4.20539 12.7131 3.79383
        12.7131 3.53999 12.4593C3.28615 12.2054 3.28615 11.7939 3.53999
        11.54L7.08039 7.99961L3.54001 4.45923C3.28617 4.20539 3.28617
        3.79383 3.54001 3.53999Z" fill="currentColor" />
    </svg>;
};

const Table = ()=>{
    const [selected, setSelected] = useState('best_use');
    const [open, setOpen] = useState(false);
    const selector_ref = useRef<HTMLDivElement | null>(null);
    const selected_label = useMemo(()=>{
        const match = columns.find(col=>col.key==selected);
        return match ? match.label : 'Best use case';
    }, [selected]);
    useEffect(()=>{
        if (!open)
            return;
        const on_click = (event: MouseEvent)=>{
            if (!selector_ref.current)
                return;
            if (!selector_ref.current.contains(event.target as Node))
                setOpen(false);
        };
        document.addEventListener('mousedown', on_click);
        return ()=>document.removeEventListener('mousedown', on_click);
    }, [open]);
    return <div className={styles.table} data-active={selected}>
      <div className={styles.right_bg} aria-hidden="true"></div>
      <div className={styles.left_spacer}></div>
      <div className={styles.right_header}>
        <div className={styles.header_labels}>
          {columns.map(col=>
            <div
              key={col.key}
              className={cn(styles.right_cell, styles.col_header,
                styles[`col_${col.key}`])}
            >
              {col.label}
            </div>
          )}
        </div>
        <div className={styles.selector} ref={selector_ref}>
          <button
            type="button"
            className={styles.selector_button}
            aria-haspopup="listbox"
            aria-expanded={open}
            onClick={()=>setOpen(!open)}
          >
            <span className={styles.selector_label}>{selected_label}</span>
            <span className={styles.selector_chevron}
              aria-hidden="true"></span>
          </button>
          {open ? <div className={styles.selector_menu} role="listbox">
            {columns.map(col=>{
              const is_selected = col.key==selected;
              return <button
                key={col.key}
                type="button"
                role="option"
                aria-selected={is_selected}
                className={cn(styles.selector_option, {
                  [styles.selector_option_selected]: is_selected,
                })}
                onClick={()=>{
                  setSelected(col.key);
                  setOpen(false);
                }}
              >
                <span className={styles.selector_option_label}>
                  {col.label}</span>
                {is_selected ? <svg className={styles.selector_check}
                  xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                  viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path fillRule="evenodd" clipRule="evenodd"
                    d="M14.4592 3.53999C14.7131 3.79383 14.7131 4.20539
                    14.4592 4.45923L6.45923 12.4592C6.20539 12.7131 5.79383
                    12.7131 5.53999 12.4592L1.53999 8.45923C1.28615 8.20539
                    1.28615 7.79383 1.53999 7.53999C1.79383 7.28615 2.20539
                    7.28615 2.45923 7.53999L5.99961 11.0804L13.54
                    3.53999C13.7938 3.28615 14.2054 3.28615 14.4592
                    3.53999Z" fill="currentColor" />
                </svg> : null}
              </button>;
            })}
          </div> : null}
        </div>
      </div>
      {rows.map((row, idx)=>{
        const is_last = idx==rows.length-1;
        return <Fragment key={row.name}>
          <div className={cn(styles.left_row, styles[`row_${row.size}`], {
            [styles.left_row_last]: is_last,
          })}>
            <span className={styles.row_title_main}>{row.name}</span>
            {row.name_note ? <span className={styles.row_title_note}>
              {row.name_note}</span> : null}
          </div>
          <div className={cn(styles.right_row, styles[`row_${row.size}`], {
            [styles.right_row_last]: is_last,
          })}>
            {columns.map(col=>{
              const value = (row as Record<string, string | boolean>)[col.key];
              return <div
                key={col.key}
                className={cn(styles.right_cell, styles.col_cell,
                  styles[`col_${col.key}`])}
              >
                {typeof value=='boolean'
                  ? <Icon type={value ? 'yes' : 'no'} /> : value}
              </div>;
            })}
          </div>
        </Fragment>;
      })}
    </div>;
};

interface i_compare_table_props {
    header?: string;
}

export const Compare_table = (props: i_compare_table_props)=>{
    return <div className={styles.container}>
      <h3 className={styles.header}>{props.header}</h3>
      <h4 className={styles.header}>{props.header}</h4>
      <Table/>
    </div>;
};

interface i_compare_table_editor_props {
    value: i_compare_table_props;
    on_change: (new_val: i_compare_table_props)=>void;
}

export const Compare_table_editor = (
props: i_compare_table_editor_props)=>{
    const {value, on_change} = props;
    return <Editor value={value} on_change={on_change} editors={[
      {label: 'Header', type: 'text', key: 'header'},
    ]} />;
};
